import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { PersistedClient } from '@tanstack/react-query-persist-client';

/**
 * Query keys excluded from sessionStorage persistence so they always re-fetch
 * on the next document load (hard refresh, browser reload, `window.location`
 * navigation, LSF page-refresh flow, etc.). Two reasons a query belongs in
 * this list:
 *
 *   1. The data changes server-side without the client knowing — stale-on-
 *      refresh is a real defect.
 *   2. The data contains values that don't survive JSON round-trip cleanly
 *      (e.g. Error instances → '{}'), which can cause UI flicker.
 *
 * Per-key rationale:
 *   - `userTasks`: fast-changing data (task list reflects what the user is
 *     working on right now; instant freshness is the design goal).
 *   - `metabaseConfig`: contains short-lived MMS access tokens AND per-report
 *     error state. Stale or partially-serialized errors (Error instances
 *     serialize to `{}`) cause flicker between generic and permission UIs.
 *     See `useMetabaseConfigs.ts` for the per-report error shape.
 *   - `entitledEntities` / `activeEntityEntitledApplications` (PSS-2582):
 *     PSP shell entitlement data. A user's role can change server-side; the
 *     client only learns about it via a fresh fetch. Persisting these means
 *     a hard refresh hydrates stale data inside the 2-minute staleTime
 *     window with `refetchOnMount: false`, so the sidebar can show
 *     entitlements that no longer reflect the user's actual access — an
 *     access-control risk, not just a UX wart.
 *   - `analyticsFullEntity` (PSS-2582): same entity data shape used by
 *     `useSegmentIdentify` for Segment account-trait reporting. Excluding it
 *     keeps account traits in sync with the (now-refreshed) entitlement set
 *     on hard refresh.
 *   - `entityProductEntitlements`: entity-level product entitlement data —
 *     same staleness risk as the two keys above; a stale-on-refresh
 *     entitlement is an access-control risk, not just a UX wart.
 *   - `activeEntityClaims`: the user's PLATFORM claims, which gate menu items
 *     such as Audit Logs — access-control data with the same staleness risk.
 *
 * Filtering happens on both `serialize` (future writes never persist
 * entitlements) AND `deserialize` (existing pre-fix cache strings are
 * stripped of entitlement queries when this code first runs in a tab that
 * already has a stale REACT_QUERY_OFFLINE_CACHE entry from before the fix).
 */
export const PERSISTED_QUERY_EXCLUDED_KEYS: readonly string[] = [
  'userTasks',
  'metabaseConfig',
  'entitledEntities',
  'activeEntityEntitledApplications',
  'analyticsFullEntity',
  'entityProductEntitlements',
  'activeEntityClaims',
];

const PERSISTED_QUERY_EXCLUDED_KEY_SET = new Set(PERSISTED_QUERY_EXCLUDED_KEYS);

export function filterPersistedClient(persistedClient: PersistedClient): PersistedClient {
  const filteredQueries = persistedClient.clientState.queries.filter(
    (query) => !PERSISTED_QUERY_EXCLUDED_KEY_SET.has(String(query.queryKey[0]))
  );

  return {
    ...persistedClient,
    clientState: {
      ...persistedClient.clientState,
      queries: filteredQueries,
    },
  };
}

export function serializePersistedClient(persistedClient: PersistedClient): string {
  return JSON.stringify(filterPersistedClient(persistedClient));
}

export function deserializePersistedClient(cachedString: string): PersistedClient {
  // Let JSON.parse throw on malformed input. TanStack's
  // persistQueryClientRestore catches restore errors, clears the persisted
  // cache via persister.removeClient(), and lets PersistQueryClientProvider's
  // .catch swallow the rethrow — so the app recovers with an empty cache and
  // EntityProvider's suspense queries fetch fresh. No defensive coding here.
  const persistedClient = JSON.parse(cachedString) as PersistedClient;
  return filterPersistedClient(persistedClient);
}

/**
 * Returns true if the error is a sessionStorage quota-exceeded error.
 *
 * Browsers signal a full storage as a DOMException with name
 * 'QuotaExceededError' (standard) or code 22 / name 'NS_ERROR_DOM_QUOTA_REACHED'
 * (Firefox legacy). Both variants are covered here.
 */
export function isQuotaExceededError(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false;
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    error.code === 22
  );
}

/**
 * Writes `value` to sessionStorage under `key`.
 *
 * If the write fails with a QuotaExceededError (storage full), the function
 * evicts PSP's own cache entry for that key and retries once. Evicting our
 * own (potentially large) serialised React Query cache frees space so that
 * remote apps mounted inside PSP — such as the budget wizard — can still
 * write their own sessionStorage keys (e.g. for ERP COA detection). PSP
 * continues to operate correctly from the in-memory React Query cache even
 * when the persisted snapshot is absent.
 *
 * If the retry also fails the write is silently abandoned. PSP never crashes
 * due to a full storage.
 */
export function safeSessionStorageSetItem(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    if (!isQuotaExceededError(error)) throw error;

    // Evict our own stale cache entry to reclaim space, then retry once.
    window.sessionStorage.removeItem(key);
    try {
      window.sessionStorage.setItem(key, value);
    } catch (retryError) {
      // Only swallow a second quota error — storage is still full even after
      // eviction (other apps filled it). PSP remains functional from its
      // in-memory cache; the missing persistence only affects the next
      // hard-refresh load time.
      // Any other error (e.g. SecurityError) is unexpected and re-thrown.
      if (!isQuotaExceededError(retryError)) throw retryError;
    }
  }
}

export const persister = createAsyncStoragePersister({
  storage: {
    getItem: async (key: string) => window.sessionStorage.getItem(key),
    setItem: async (key: string, value: string) => safeSessionStorageSetItem(key, value),
    removeItem: async (key: string) => window.sessionStorage.removeItem(key),
  },
  serialize: serializePersistedClient,
  deserialize: deserializePersistedClient,
});
